import React, { useRef, useState, useEffect, useCallback } from 'react';
import FaceCamera from './FaceCamera';
import { useFaceApi } from '../../hooks/useFaceApi';

const DETECTION_INTERVAL_MS = 100;  // 10 fps
const STABLE_FRAMES_NEEDED  = 5;    // frames para confirmar que hay cara
const MOVE_THRESHOLD        = 0.13; // 13% del ancho del rostro = cabeza girada

// Dirección aleatoria para cada sesión
function randomDirection() {
  return Math.random() < 0.5 ? 'left' : 'right';
}

/**
 * FaceLiveness — detección de movimiento de cabeza.
 *
 * Flujo:
 *  1. loading      — carga modelos
 *  2. positioning  — detecta cara estable (5 frames)
 *  3. move         — pide girar la cabeza a una dirección aleatoria (← o →)
 *                    detecta cuando la nariz se desplaza >13% del ancho del rostro
 *  4. confirming   — captura descriptor
 *  5. done         — llama onConfirmed(descriptor)
 *
 * Una foto impresa no puede girar → anti-spoofing real y rápido.
 */
function FaceLiveness({ onConfirmed, onError, title = 'Verificación facial' }) {
  const { modelsLoaded, loadError, detectFace } = useFaceApi();
  const videoRef    = useRef(null);
  const intervalRef = useRef(null);

  const [phase, setPhase]         = useState('loading');
  const [direction, setDirection] = useState('left');  // dirección pedida al usuario

  const stableFrames    = useRef(0);
  const baselineNose    = useRef(null);   // posición relativa de la nariz en reposo (0-1)
  const lastDescriptor  = useRef(null);
  const directionRef    = useRef('left'); // ref para acceder dentro del interval sin stale closure

  const stopInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Obtiene la posición relativa de la punta de la nariz dentro del bounding box del rostro
  // Devuelve un valor 0-1 donde 0.5 = frente al centro, <0.5 = girado a la izquierda
  function getNoseRelX(detection) {
    const box     = detection.detection.box;
    const noseTip = detection.landmarks.positions[30]; // landmark 30 = punta de la nariz
    return (noseTip.x - box.x) / box.width;
  }

  useEffect(() => {
    if (modelsLoaded) setPhase('positioning');
    if (loadError)   { setPhase('error'); onError?.(loadError); }
  }, [modelsLoaded, loadError, onError]);

  useEffect(() => {
    const activePhases = ['positioning', 'move'];
    if (!activePhases.includes(phase)) { stopInterval(); return; }

    const video = videoRef.current?.video;
    if (!video) return;

    intervalRef.current = setInterval(async () => {
      const detection = await detectFace(video);

      // ── POSITIONING ────────────────────────────────────────────
      if (phase === 'positioning') {
        if (detection) {
          stableFrames.current += 1;
          if (stableFrames.current >= STABLE_FRAMES_NEEDED) {
            // Cara estable — registrar posición base de la nariz
            baselineNose.current = getNoseRelX(detection);
            lastDescriptor.current = Array.from(detection.descriptor);

            // Elegir dirección aleatoria y arrancar la fase de movimiento
            const dir = randomDirection();
            directionRef.current = dir;
            setDirection(dir);
            stableFrames.current = 0;
            setPhase('move');
          }
        } else {
          stableFrames.current = Math.max(0, stableFrames.current - 1);
        }
        return;
      }

      // ── MOVE ───────────────────────────────────────────────────
      if (phase === 'move') {
        if (!detection) {
          // Perdió la cara — volver a posicionar
          baselineNose.current = null;
          stableFrames.current = 0;
          setPhase('positioning');
          return;
        }

        // Actualizar descriptor con el frame más reciente
        lastDescriptor.current = Array.from(detection.descriptor);

        const noseRel  = getNoseRelX(detection);
        const baseline = baselineNose.current;
        const delta    = noseRel - baseline; // positivo = giró a la derecha, negativo = izquierda

        const movedLeft  = delta < -MOVE_THRESHOLD;
        const movedRight = delta >  MOVE_THRESHOLD;
        const moved = directionRef.current === 'left' ? movedLeft : movedRight;

        if (moved) {
          stopInterval();
          setPhase('confirming');
          const descriptor = lastDescriptor.current;
          setTimeout(() => {
            setPhase('done');
            onConfirmed?.(descriptor);
          }, 350);
        }
      }
    }, DETECTION_INTERVAL_MS);

    return stopInterval;
  }, [phase, detectFace, onConfirmed, stopInterval]);

  // ── UI ──────────────────────────────────────────────────────────
  const arrow = direction === 'left' ? '←' : '→';
  const dirLabel = direction === 'left' ? 'izquierda' : 'derecha';

  return (
    <div className="face-liveness">
      {title && <h3 className="face-liveness-title">{title}</h3>}

      <div className={`face-camera-container face-phase-${phase}`}>
        <FaceCamera
          ref={videoRef}
          width={300}
          height={225}
          onReady={() => {}}
          onError={(e) => { setPhase('error'); onError?.(e); }}
        />

        {/* Óvalo guía */}
        <div className="face-overlay">
          <div className={[
            'face-oval',
            phase === 'move'                                 ? 'face-oval-ready' : '',
            phase === 'confirming' || phase === 'done'       ? 'face-oval-done'  : '',
          ].join(' ')} />

          {/* Flecha de dirección superpuesta */}
          {phase === 'move' && (
            <div className={`face-arrow face-arrow-${direction}`}>
              {arrow}
            </div>
          )}
        </div>

        {/* Checkmark de confirmación */}
        {(phase === 'confirming' || phase === 'done') && (
          <div className="face-confirm-check">✓</div>
        )}
      </div>

      {/* Texto de estado */}
      <div className={`face-status face-status-${phase}`}>
        {phase === 'loading' && (
          <>
            <span className="face-status-icon">⏳</span>
            <span className="face-status-text">Cargando...</span>
          </>
        )}
        {phase === 'positioning' && (
          <>
            <span className="face-status-icon">👤</span>
            <span className="face-status-text">Centra tu rostro en el recuadro</span>
            <span className="face-status-sub">Mira directo a la cámara</span>
          </>
        )}
        {phase === 'move' && (
          <>
            <span className="face-status-icon" style={{ fontSize: 28 }}>{arrow}</span>
            <span className="face-status-text">Gira la cabeza a la {dirLabel}</span>
            <span className="face-status-sub">Solo un poco — no necesitas exagerar</span>
          </>
        )}
        {(phase === 'confirming' || phase === 'done') && (
          <>
            <span className="face-status-icon">✅</span>
            <span className="face-status-text">¡Verificado!</span>
          </>
        )}
        {phase === 'error' && (
          <>
            <span className="face-status-icon">❌</span>
            <span className="face-status-text">{loadError || 'Error de cámara'}</span>
          </>
        )}
      </div>
    </div>
  );
}

export default FaceLiveness;
